<template>
  <div class="gl-branches">
    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Branches</span>
          </div>
          <h1 class="gl-branch-title">My Branches</h1>
        </div>
        <router-link to="/owner/branches/create" class="gl-add-btn gl-header-add-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Branch
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ branches.length }}</span>
          <span class="gl-stat-lbl">Total</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-active">{{ branches.filter(b => b.is_active).length }}</span>
          <span class="gl-stat-lbl">Active</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-inactive">{{ branches.filter(b => !b.is_active).length }}</span>
          <span class="gl-stat-lbl">Inactive</span>
        </div>
        <div class="gl-stat-spacer"></div>
        <!-- View toggle -->
        <div class="gl-view-toggle">
          <button :class="['gl-vt-btn', { active: viewMode === 'cards' }]" @click="viewMode = 'cards'" title="Card view">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="2" width="9" height="9" rx="2"/><rect x="13" y="2" width="9" height="9" rx="2"/><rect x="2" y="13" width="9" height="9" rx="2"/><rect x="13" y="13" width="9" height="9" rx="2"/></svg>
          </button>
          <button :class="['gl-vt-btn', { active: viewMode === 'table' }]" @click="viewMode = 'table'" title="Table view">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- ───────── SKELETON ───────── -->
      <template v-if="loading">
        <div class="gl-cards-grid">
          <div v-for="i in 4" :key="i" class="gl-branch-card gl-branch-card--skel" :style="`animation-delay:${(i-1)*60}ms`">
            <div class="gl-bc-top">
              <div class="gl-skel gl-skel-avatar"></div>
              <div style="flex:1">
                <div class="gl-skel gl-skel-h" style="width:55%;margin-bottom:8px"></div>
                <div class="gl-skel gl-skel-s" style="width:35%"></div>
              </div>
              <div class="gl-skel gl-skel-badge-s"></div>
            </div>
            <div class="gl-bc-divider"></div>
            <div class="gl-bc-meta">
              <div class="gl-skel gl-skel-s" style="width:80%;margin-bottom:10px"></div>
              <div class="gl-skel gl-skel-s" style="width:50%"></div>
            </div>
            <div class="gl-bc-actions-skel">
              <div class="gl-skel gl-skel-btn-flex"></div>
              <div class="gl-skel gl-skel-btn-flex"></div>
              <div class="gl-skel gl-skel-btn-sq"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ───────── CARD VIEW ───────── -->
      <template v-else-if="viewMode === 'cards'">
        <div v-if="branches.length" class="gl-cards-grid">
          <div
            v-for="(branch, i) in branches"
            :key="branch.id"
            class="gl-branch-card"
            :style="`animation-delay:${i * 60}ms`"
          >
            <div class="gl-bc-top">
              <div class="gl-bc-avatar">{{ branch.name.charAt(0).toUpperCase() }}{{ (branch.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
              <div class="gl-bc-identity">
                <div class="gl-bc-name">{{ branch.name }}</div>
                <div class="gl-bc-slug">{{ branch.slug }}</div>
              </div>
              <div :class="['gl-status-badge', branch.is_active ? 'gl-status-active' : 'gl-status-inactive']">
                <span class="gl-status-dot"></span>
                {{ branch.is_active ? 'Active' : 'Inactive' }}
              </div>
            </div>

            <div class="gl-bc-divider"></div>

            <div class="gl-bc-meta">
              <div class="gl-bc-meta-row">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="gl-meta-icon"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>{{ branch.address }}, {{ branch.city }}, {{ branch.state }}</span>
              </div>
              <div class="gl-bc-meta-row">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="gl-meta-icon"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.14 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.05 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16z"/></svg>
                <span>{{ branch.phone }}</span>
              </div>
            </div>

            <div class="gl-bc-actions">
              <router-link :to="`/owner/branches/${branch.id}/edit`" class="gl-bc-btn gl-bc-btn-edit" title="Edit">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </router-link>
              <router-link :to="`/owner/branches/${branch.id}/staff`" class="gl-bc-btn gl-bc-btn-staff" title="Manage Staff">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Staff
              </router-link>
              <button @click="confirmDelete(branch)" class="gl-bc-btn gl-bc-btn-delete" title="Delete">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="gl-empty">
          <div class="gl-empty-icon">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          </div>
          <div class="gl-empty-title">No branches yet</div>
          <div class="gl-empty-sub">Add your first branch to start managing locations.</div>
          <router-link to="/owner/branches/create" class="gl-add-btn gl-add-btn-sm">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Branch
          </router-link>
        </div>
      </template>

      <!-- ───────── TABLE VIEW ───────── -->
      <template v-else>
        <div class="card">
          <div class="card-body p-0">
            <div v-if="branches.length" class="table-responsive">
              <table class="table gl-branch-table mb-0">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="branch in branches" :key="branch.id" class="gl-tr">
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="gl-bc-avatar gl-bc-avatar-sm">{{ branch.name.charAt(0).toUpperCase() }}{{ (branch.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
                        <div>
                          <div class="fw-600">{{ branch.name }}</div>
                          <div class="gl-tbl-slug">{{ branch.slug }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="gl-tbl-cell">{{ branch.address }}</td>
                    <td class="gl-tbl-cell">{{ branch.city }}, {{ branch.state }}</td>
                    <td class="gl-tbl-cell">{{ branch.phone }}</td>
                    <td>
                      <div :class="['gl-status-badge', branch.is_active ? 'gl-status-active' : 'gl-status-inactive']">
                        <span class="gl-status-dot"></span>
                        {{ branch.is_active ? 'Active' : 'Inactive' }}
                      </div>
                    </td>
                    <td class="text-end">
                      <div class="d-flex align-items-center justify-content-end gap-1">
                        <router-link :to="`/owner/branches/${branch.id}/edit`" class="gl-tbl-btn gl-tbl-btn-edit" title="Edit">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </router-link>
                        <router-link :to="`/owner/branches/${branch.id}/staff`" class="gl-tbl-btn gl-tbl-btn-staff" title="Staff">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        </router-link>
                        <button @click="confirmDelete(branch)" class="gl-tbl-btn gl-tbl-btn-del" title="Delete">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="gl-empty">
              <div class="gl-empty-icon">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div class="gl-empty-title">No branches yet</div>
              <div class="gl-empty-sub">Add your first branch to start managing locations.</div>
              <router-link to="/owner/branches/create" class="gl-add-btn gl-add-btn-sm mt-3">+ Add Branch</router-link>
            </div>
          </div>
        </div>
      </template>

      <!-- Pagination -->
      <div v-if="!loading && pagination.last_page > 1" class="gl-pagination">
        <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchBranches(pagination.current_page - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button
          v-for="page in pagination.last_page" :key="page"
          :class="['gl-page-num', { active: page === pagination.current_page }]"
          @click="fetchBranches(page)"
        >{{ page }}</button>
        <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchBranches(pagination.current_page + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>

    <!-- Mobile FAB -->
    <router-link to="/owner/branches/create" class="gl-mobile-fab" aria-label="Add Branch">
      <span class="gl-fab-inner">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        <span class="gl-fab-label">Add Branch</span>
      </span>
    </router-link>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="gl-modal-backdrop" @click.self="showDeleteModal = false">
      <div class="gl-modal">
        <div class="gl-modal-icon gl-modal-icon-danger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        </div>
        <h2 class="gl-modal-title">Delete Branch</h2>
        <p class="gl-modal-body">
          Are you sure you want to delete <strong>{{ selectedBranch?.name }}</strong>? This action cannot be undone.
        </p>
        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showDeleteModal = false">Cancel</button>
          <button class="gl-modal-confirm" @click="deleteBranch" :disabled="deleting">
            <span v-if="deleting" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Delete Branch</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ownerBranchApi } from '@/services/ownerApi';

export default {
  name: 'BranchList',

  data() {
    return {
      loading: true,
      deleting: false,
      viewMode: 'cards',
      branches: [],
      selectedBranch: null,
      showDeleteModal: false,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
      }
    };
  },

  mounted() {
    this.fetchBranches();
    if (window.innerWidth <= 767) this.viewMode = 'cards';
    this._onResize = () => { if (window.innerWidth <= 767) this.viewMode = 'cards'; };
    window.addEventListener('resize', this._onResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this._onResize);
  },

  methods: {
    async fetchBranches(page = 1) {
      try {
        this.loading = true;
        const response = await ownerBranchApi.list({ page });
        this.branches = response.data.data;
        if (response.data.meta) {
          this.pagination = {
            current_page: response.data.meta.current_page,
            last_page: response.data.meta.last_page,
            per_page: response.data.meta.per_page,
            total: response.data.meta.total
          };
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(branch) {
      this.selectedBranch = branch;
      this.showDeleteModal = true;
    },

    async deleteBranch() {
      if (!this.selectedBranch) return;
      try {
        this.deleting = true;
        await ownerBranchApi.delete(this.selectedBranch.id);
        this.showDeleteModal = false;
        this.fetchBranches(this.pagination.current_page);
      } catch (error) {
        console.error('Failed to delete branch:', error);
        alert(error.response?.data?.message || 'Failed to delete branch');
      } finally {
        this.deleting = false;
        this.selectedBranch = null;
      }
    }
  }
};
</script>

<style scoped>
/* ── Layout ── */
.gl-branches { padding-bottom: 40px; }

.gl-branch-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 28px 0 20px;
}
.gl-branch-breadcrumb {
  font-size: 12.5px;
  color: #9ca3af;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #900C27; }
.gl-bc-sep { opacity: 0.5; }
.gl-branch-title {
  font-size: 22px;
  font-weight: 700;
  color: #111318;
  margin: 0;
  letter-spacing: -0.4px;
}

/* ── Add button ── */
.gl-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #900C27;
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: 10px;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-add-btn:hover {
  background: #6f0a1e;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(144,12,39,0.30);
}
.gl-add-btn-sm { font-size: 13px; padding: 8px 16px; }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; gap: 20px;
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 14px; padding: 12px 18px; margin-bottom: 20px;
}
.gl-stat-chip { display: flex; align-items: center; gap: 7px; }
.gl-stat-num { font-size: 18px; font-weight: 700; color: #111318; line-height: 1; }
.gl-stat-num.gl-stat-active { color: #900C27; }
.gl-stat-num.gl-stat-inactive { color: #9ca3af; }
.gl-stat-lbl { font-size: 12px; color: #9ca3af; font-weight: 500; }
.gl-stat-divider { width: 1px; height: 20px; background: #e5e7eb; }
.gl-stat-spacer { flex: 1; }

/* ── View toggle ── */
.gl-view-toggle { display: flex; background: #f3f4f6; border-radius: 8px; padding: 3px; gap: 2px; }
.gl-vt-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 26px; border: none; background: transparent;
  color: #9ca3af; border-radius: 6px; cursor: pointer; transition: all 0.15s;
}
.gl-vt-btn.active { background: #fff; color: #900C27; box-shadow: 0 1px 4px rgba(0,0,0,0.10); }
.gl-vt-btn:hover:not(.active) { color: #374151; }

/* ── Cards grid ── */
.gl-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

/* ── Branch card ── */
.gl-branch-card {
  background: #fff;
  border-radius: 16px;
  border: 1.5px solid #f0f1f3;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  animation: gl-card-in 0.35s ease both;
  display: flex;
  flex-direction: column;
  gap: 0;
}
@keyframes gl-card-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-branch-card:hover {
  border-color: rgba(144,12,39,0.3);
  box-shadow: 0 4px 20px rgba(0,0,0,0.07);
  transform: translateY(-2px);
}

/* ── Card top ── */
.gl-bc-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.gl-bc-avatar {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #900C27, #6f0a1e);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}
.gl-bc-avatar-sm {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  font-size: 11px;
}
.gl-bc-identity { flex: 1; min-width: 0; }
.gl-bc-name {
  font-size: 14.5px;
  font-weight: 700;
  color: #111318;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gl-bc-slug {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 1px;
}

/* ── Status badge ── */
.gl-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.gl-status-active {
  background: rgba(144,12,39,0.10);
  color: #900C27;
}
.gl-status-inactive {
  background: #f3f4f6;
  color: #9ca3af;
}
.gl-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.gl-status-active .gl-status-dot {
  animation: gl-pulse 2s ease infinite;
  box-shadow: 0 0 0 0 rgba(144,12,39,0.4);
}
@keyframes gl-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(144,12,39,0.4); }
  70%  { box-shadow: 0 0 0 5px rgba(144,12,39,0); }
  100% { box-shadow: 0 0 0 0 rgba(144,12,39,0); }
}

/* ── Divider ── */
.gl-bc-divider {
  height: 1px;
  background: #f0f1f3;
  margin-bottom: 14px;
}

/* ── Meta rows ── */
.gl-bc-meta { display: flex; flex-direction: column; gap: 8px; flex: 1; margin-bottom: 16px; }
.gl-bc-meta-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.4;
}
.gl-meta-icon { flex-shrink: 0; margin-top: 1px; color: #9ca3af; }

/* ── Card actions ── */
.gl-bc-actions {
  display: flex;
  gap: 6px;
}
.gl-bc-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
  flex: 1;
  justify-content: center;
}
.gl-bc-btn-edit {
  background: rgba(144,12,39,0.08);
  color: #900C27;
  border-color: rgba(144,12,39,0.15);
}
.gl-bc-btn-edit:hover { background: #900C27; color: #fff; border-color: #900C27; }
.gl-bc-btn-staff {
  background: rgba(59,130,246,0.08);
  color: #3b82f6;
  border-color: rgba(59,130,246,0.15);
}
.gl-bc-btn-staff:hover { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.gl-bc-btn-delete {
  background: rgba(239,68,68,0.08);
  color: #ef4444;
  border-color: rgba(239,68,68,0.15);
  flex: 0 0 auto;
  padding: 6px 10px;
}
.gl-bc-btn-delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }

/* ── Table view ── */
.gl-branch-table thead th {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9ca3af;
  border-bottom: 1.5px solid #f0f1f3;
  padding: 14px 16px;
  background: #fafafa;
}
.card-body .table-responsive { border-radius: 12px !important; overflow: hidden !important; }
.gl-branch-table tbody td {
  padding: 14px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #f5f6f7;
  font-size: 13.5px;
}
.gl-tr { transition: background 0.1s; }
.gl-tr:hover td { background: #fafcfb; }
.gl-tbl-slug { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.gl-tbl-cell { color: #6b7280; }
.fw-600 { font-weight: 600; color: #111318; }

.gl-tbl-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 7px; border: 1.5px solid transparent;
  text-decoration: none; transition: all 0.15s; background: transparent; cursor: pointer;
}
.gl-tbl-btn-edit { color: #900C27; border-color: rgba(144,12,39,0.2); background: rgba(144,12,39,0.06); }
.gl-tbl-btn-edit:hover { background: #900C27; color: #fff; border-color: #900C27; }
.gl-tbl-btn-staff { color: #3b82f6; border-color: rgba(59,130,246,0.2); background: rgba(59,130,246,0.06); }
.gl-tbl-btn-staff:hover { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.gl-tbl-btn-del { color: #ef4444; border-color: rgba(239,68,68,0.2); background: rgba(239,68,68,0.06); }
.gl-tbl-btn-del:hover { background: #ef4444; color: #fff; border-color: #ef4444; }

/* ── Empty state ── */
.gl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}
.gl-empty-icon {
  width: 64px; height: 64px; border-radius: 18px;
  background: rgba(144,12,39,0.08);
  display: flex; align-items: center; justify-content: center;
  color: #900C27; margin-bottom: 16px;
}
.gl-empty-title { font-size: 16px; font-weight: 700; color: #111318; margin-bottom: 6px; }
.gl-empty-sub { font-size: 13.5px; color: #9ca3af; margin-bottom: 20px; }

/* ── Pagination ── */
.gl-pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 4px; margin-top: 24px;
}
.gl-page-btn {
  width: 34px; height: 34px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff;
  display: flex; align-items: center; justify-content: center;
  color: #6b7280; cursor: pointer; transition: all 0.15s;
}
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gl-page-num {
  min-width: 34px; height: 34px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; padding: 0 8px;
}
.gl-page-num.active { background: #900C27; border-color: #900C27; color: #fff; }
.gl-page-num:hover:not(.active) { border-color: #900C27; color: #900C27; }

/* ── Modal ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; backdrop-filter: blur(4px);
  animation: gl-backdrop-in 0.15s ease;
}
@keyframes gl-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
.gl-modal {
  background: #fff; border-radius: 20px;
  padding: 32px 28px; width: 100%; max-width: 400px;
  margin: 16px; text-align: center;
  animation: gl-modal-in 0.2s cubic-bezier(0.34,1.56,0.64,1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}
@keyframes gl-modal-in {
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.gl-modal-icon {
  width: 52px; height: 52px; border-radius: 16px; margin: 0 auto 16px;
  display: flex; align-items: center; justify-content: center;
}
.gl-modal-icon-danger { background: rgba(239,68,68,0.10); color: #ef4444; }
.gl-modal-title { font-size: 17px; font-weight: 700; color: #111318; margin: 0 0 8px; }
.gl-modal-body { font-size: 13.5px; color: #6b7280; margin: 0 0 24px; line-height: 1.5; }
.gl-modal-body strong { color: #111318; }
.gl-modal-actions { display: flex; gap: 10px; }
.gl-modal-cancel {
  flex: 1; height: 40px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-modal-cancel:hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.gl-modal-confirm {
  flex: 1; height: 40px; border-radius: 10px; border: none;
  background: #ef4444; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: background 0.15s;
}
.gl-modal-confirm:hover:not(:disabled) { background: #dc2626; }
.gl-modal-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Mobile FAB (hidden on desktop) ── */
.gl-mobile-fab { display: none; }

/* ── Skeleton ── */
.gl-branch-card--skel { pointer-events: none; animation: gl-card-in 0.35s ease both; }
.gl-skel {
  background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%);
  background-size: 200% 100%;
  animation: gl-shimmer 1.4s ease infinite;
  border-radius: 6px;
}
@keyframes gl-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.gl-skel-avatar { width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0; }
.gl-skel-h { height: 16px; }
.gl-skel-s { height: 11px; }
.gl-skel-badge-s { width: 60px; height: 24px; border-radius: 20px; flex-shrink: 0; }
.gl-bc-actions-skel { display: flex; gap: 6px; }
.gl-skel-btn-flex { flex: 1; height: 32px; border-radius: 8px; }
.gl-skel-btn-sq { flex: 0 0 32px; width: 32px; height: 32px; border-radius: 8px; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  /* Header: hide desktop add button, compact spacing */
  .gl-header-add-btn { display: none !important; }
  .gl-branch-header { padding: 14px 0 12px; }
  .gl-branch-title { font-size: 19px !important; letter-spacing: -0.3px; }
  .gl-branch-breadcrumb { font-size: 11px; }

  /* Stats strip: compact */
  .gl-stats-strip {
    padding: 10px 14px;
    gap: 12px;
    border-radius: 12px;
    margin-bottom: 14px;
  }
  .gl-stat-num { font-size: 16px; }
  .gl-stat-lbl { font-size: 11px; }

  /* Hide view toggle — always cards on mobile */
  .gl-view-toggle { display: none !important; }

  /* Single-column card grid */
  .gl-cards-grid { grid-template-columns: 1fr; gap: 10px; }

  /* Compact card */
  .gl-branch-card { padding: 15px 16px; border-radius: 14px; }
  .gl-bc-top { margin-bottom: 11px; }
  .gl-bc-avatar { width: 38px; height: 38px; border-radius: 10px; font-size: 12px; }
  .gl-bc-name { font-size: 14px; }

  /* Touch-friendly action buttons */
  .gl-bc-actions { gap: 7px; }
  .gl-bc-btn {
    padding: 9px 10px;
    font-size: 12px;
    border-radius: 9px;
    flex: 1;
  }
  .gl-bc-btn-delete {
    flex: 0 0 38px;
    padding: 9px 0;
    width: 38px;
  }

  /* FAB */
  .gl-mobile-fab {
    display: flex;
    position: fixed;
    bottom: 24px;
    right: 20px;
    z-index: 200;
    text-decoration: none;
  }
  .gl-fab-inner {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #900C27;
    color: #fff;
    padding: 13px 20px 13px 16px;
    border-radius: 100px;
    font-size: 13.5px;
    font-weight: 700;
    box-shadow: 0 6px 24px rgba(144,12,39,0.42), 0 2px 6px rgba(0,0,0,0.12);
    transition: transform 0.15s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.15s;
    letter-spacing: 0.1px;
  }
  .gl-mobile-fab:active .gl-fab-inner { transform: scale(0.94); }
  .gl-mobile-fab:hover .gl-fab-inner {
    box-shadow: 0 10px 32px rgba(144,12,39,0.50), 0 2px 8px rgba(0,0,0,0.14);
    transform: translateY(-2px);
  }
  .gl-fab-label { color: #fff; }

  /* Modal — bottom sheet, clear of navbar */
  .gl-modal-backdrop { padding: 68px 16px 16px !important; align-items: center !important; }
  .gl-modal { border-radius: 16px !important; max-height: calc(100vh - 84px) !important; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-branch-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-branch-breadcrumb { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-branch-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-stats-strip { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-stat-num { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-stat-lbl { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stat-divider { background: #3d4255 !important; }
body.dark-only .gl-view-toggle { background: rgba(255,255,255,0.06) !important; }
body.dark-only .gl-vt-btn.active { background: #323644 !important; color: #900C27 !important; }
body.dark-only .gl-vt-btn:hover:not(.active) { color: rgba(255,255,255,0.7) !important; }

body.dark-only .gl-branch-card {
  background: #262932 !important;
  border-color: #323644 !important;
}
body.dark-only .gl-branch-card:hover {
  border-color: rgba(144,12,39,0.4) !important;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25) !important;
}
body.dark-only .gl-bc-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-bc-slug { color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-bc-divider { background: #323644 !important; }
body.dark-only .gl-bc-meta-row { color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-meta-icon { color: rgba(255,255,255,0.25) !important; }
body.dark-only .gl-status-inactive { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.35) !important; }

body.dark-only .gl-bc-btn-edit { background: rgba(144,12,39,0.12) !important; color: #4caf97 !important; border-color: rgba(144,12,39,0.2) !important; }
body.dark-only .gl-bc-btn-edit:hover { background: #900C27 !important; color: #fff !important; }
body.dark-only .gl-bc-btn-staff { background: rgba(59,130,246,0.10) !important; color: #60a5fa !important; border-color: rgba(59,130,246,0.2) !important; }
body.dark-only .gl-bc-btn-staff:hover { background: #3b82f6 !important; color: #fff !important; }
body.dark-only .gl-bc-btn-delete { background: rgba(239,68,68,0.10) !important; color: #f87171 !important; border-color: rgba(239,68,68,0.2) !important; }
body.dark-only .gl-bc-btn-delete:hover { background: #ef4444 !important; color: #fff !important; }

body.dark-only .gl-branch-table thead th { background: #1d1e26 !important; border-bottom-color: #323644 !important; }
body.dark-only .gl-branch-table tbody td { border-bottom-color: #2d3044 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .fw-600 { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-tbl-slug { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-tr:hover td { background: rgba(255,255,255,0.03) !important; }
body.dark-only .gl-tbl-btn-edit { background: rgba(144,12,39,0.10) !important; color: #4caf97 !important; border-color: rgba(144,12,39,0.2) !important; }
body.dark-only .gl-tbl-btn-edit:hover { background: #900C27 !important; color: #fff !important; border-color: #900C27 !important; }
body.dark-only .gl-tbl-btn-staff { background: rgba(59,130,246,0.08) !important; color: #60a5fa !important; border-color: rgba(59,130,246,0.2) !important; }
body.dark-only .gl-tbl-btn-staff:hover { background: #3b82f6 !important; color: #fff !important; }
body.dark-only .gl-tbl-btn-del { background: rgba(239,68,68,0.08) !important; color: #f87171 !important; border-color: rgba(239,68,68,0.2) !important; }
body.dark-only .gl-tbl-btn-del:hover { background: #ef4444 !important; color: #fff !important; }

body.dark-only .gl-page-btn,
body.dark-only .gl-page-num { background: #262932 !important; border-color: #323644 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-page-num.active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; }

body.dark-only .gl-modal { background: #262932 !important; }
body.dark-only .gl-modal-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-modal-body { color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-modal-body strong { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-modal-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.6) !important; }

body.dark-only .gl-empty-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-empty-sub { color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #2d3044 25%, #323644 50%, #2d3044 75%) !important; background-size: 200% 100% !important; }
body.dark-only .gl-fab-inner { box-shadow: 0 6px 24px rgba(144,12,39,0.35), 0 2px 6px rgba(0,0,0,0.30) !important; }
.gl-mobile-fab { background: transparent !important; border: none !important; box-shadow: none !important; }
</style>
